import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { GeneralService, Currency } from '../general/general.service';

/**
 * Currency preference interface for user selection
 */
export interface CurrencyPreference extends Currency {
  uuid: string;
  name: string;
  exchangeRate: string;
}

/**
 * Currency service for managing global currency selection and conversion
 */
@Injectable({
  providedIn: 'root'
})
export class CurrencyService {
  private readonly STORAGE_KEY = 'selectedCurrency';
  private readonly USER_CHOSEN_KEY = 'selectedCurrencyUserChosen';
  private readonly DEFAULT_CURRENCY_NAME = 'USD';

  /**
   * BehaviorSubject for current selected currency
   */
  private currentCurrencySubject = new BehaviorSubject<CurrencyPreference | null>(null);

  /**
   * BehaviorSubject for available currencies from API
   */
  private availableCurrenciesSubject = new BehaviorSubject<Currency[]>([]);

  /**
   * Public observable for current currency (reactive)
   */
  currentCurrency$ = this.currentCurrencySubject.asObservable();

  /**
   * Public observable for available currencies (reactive)
   */
  availableCurrencies$ = this.availableCurrenciesSubject.asObservable();

  /**
   * Flag to prevent double initialization
   */
  private isInitialized = false;

  constructor(private generalService: GeneralService) {}

  /**
   * Initialize available currencies from GeneralService and apply a USD default.
   * Called during app startup (APP_INITIALIZER) before auth completes.
   * User-specific currency (globalCurrency) is applied later via applyUserCurrency()
   * once auth is confirmed.
   */
  initializeCurrencies(): Promise<void> {
    if (this.isInitialized) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.generalService.currencies$.subscribe((currencies) => {
        if (currencies && currencies.length > 0) {
          this.availableCurrenciesSubject.next(currencies);
          this.setInitialCurrency(currencies, null);
          this.isInitialized = true;
          resolve();
        }
      });
    });
  }

  /**
   * Apply the user's globalCurrency from get-user-info after auth is confirmed.
   * Skipped if the user has already explicitly chosen a currency this session.
   * @param globalCurrency - Currency code from get-user-info (e.g. 'CHF'), or null
   */
  applyUserCurrency(globalCurrency: string | null): void {
    const isUserChosen = localStorage.getItem(this.USER_CHOSEN_KEY) === 'true';
    if (isUserChosen) return;

    const currencies = this.availableCurrenciesSubject.value;
    if (!currencies.length) return;

    const preferred = globalCurrency ? currencies.find(c => c.name === globalCurrency) : null;
    const resolved = preferred
      ?? currencies.find(c => c.name === this.DEFAULT_CURRENCY_NAME)
      ?? currencies[0];

    if (resolved) {
      this.currentCurrencySubject.next(resolved as CurrencyPreference);
    }
  }

  /**
   * Get current currency value (synchronous)
   */
  getCurrentCurrency(): CurrencyPreference | null {
    return this.currentCurrencySubject.value;
  }

  /**
   * Get available currencies (synchronous)
   */
  getAvailableCurrencies(): Currency[] {
    return this.availableCurrenciesSubject.value;
  }

  /**
   * Set selected currency and persist to localStorage so the choice survives page refreshes
   * within the same login session.
   * @param currency - The currency to set as selected
   */
  setSelectedCurrency(currency: CurrencyPreference): void {
    this.currentCurrencySubject.next(currency);
    this.storeCurrency(currency);
    localStorage.setItem(this.USER_CHOSEN_KEY, 'true');
  }

  /**
   * Clear the stored currency preference from localStorage.
   * Should be called on logout so the next login starts fresh from the user's globalCurrency.
   */
  clearStoredCurrency(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.USER_CHOSEN_KEY);
  }

  /**
   * Convert amount using current currency's exchange rate
   * @param amount - The amount to convert
   * @param fromExchangeRate - Optional source exchange rate (for reverse conversion)
   * @returns Converted amount
   */
  convertAmount(amount: number, fromExchangeRate?: string): number {
    const currentCurrency = this.getCurrentCurrency();

    if (!currentCurrency || !currentCurrency.exchangeRate) {
      return amount; // No conversion needed
    }

    try {
      const exchangeRate = parseFloat(currentCurrency.exchangeRate);

      if (isNaN(exchangeRate) || exchangeRate === 0) {
        console.warn('Invalid exchange rate:', currentCurrency.exchangeRate);
        return amount;
      }

      // If fromExchangeRate is provided, we need to convert from source to target
      if (fromExchangeRate) {
        const fromRate = parseFloat(fromExchangeRate);

        if (isNaN(fromRate) || fromRate === 0) {
          return amount;
        }

        return (amount / fromRate) * exchangeRate;
      }

      // Otherwise, just multiply by the target exchange rate
      return amount * exchangeRate;
    } catch (error) {
      console.error('Error converting currency:', error);
      return amount;
    }
  }

  /**
   * Convert amount from a specific currency to the currently selected currency
   * @param amount - The amount to convert
   * @param fromCurrencyCode - The source currency code (e.g., 'MXN', 'USD')
   * @returns Converted amount in the selected currency, or original amount if conversion not possible
   */
  convertFromCurrency(amount: number, fromCurrencyCode: string): number {
    const selectedCurrency = this.getCurrentCurrency();

    // If no selected currency or currencies match, no conversion needed
    if (!selectedCurrency || selectedCurrency.name === fromCurrencyCode) {
      return amount;
    }

    // Get the source currency's exchange rate
    const availableCurrencies = this.getAvailableCurrencies();
    const sourceCurrency = availableCurrencies.find(c => c.name === fromCurrencyCode);

    if (!sourceCurrency || !sourceCurrency.exchangeRate) {
      return amount;
    }

    // Use the existing convertAmount method with the source exchange rate
    return this.convertAmount(amount, sourceCurrency.exchangeRate);
  }

  /**
   * Get currency symbol for a given currency code
   * @param currencyName - Currency code (e.g., 'USD', 'EUR')
   * @returns Currency symbol (e.g., '$', '€')
   */
  getCurrencySymbol(currencyName: string): string {
    const symbolMap = this.getCurrencySymbolMap();
    return symbolMap[currencyName] || currencyName;
  }

  /**
   * Get mapping of currency codes to symbols
   * @returns Record of currency code to symbol
   */
  getCurrencySymbolMap(): Record<string, string> {
    return {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CNY': '¥',
      'INR': '₹',
      'AUD': 'A$',
      'CAD': 'C$',
      'CHF': 'Fr',
      'SEK': 'kr',
      'NZD': 'NZ$',
      'KRW': '₩',
      'SGD': 'S$',
      'NOK': 'kr',
      'MXN': '$',
      'ZAR': 'R',
      'HKD': 'HK$',
      'BRL': 'R$',
      'RUB': '₽',
      'TRY': '₺',
      'PLN': 'zł',
      'THB': '฿',
      'IDR': 'Rp',
      'MYR': 'RM',
      'PHP': '₱',
      'DKK': 'kr',
      'CZK': 'Kč',
      'ILS': '₪',
      'CLP': '$',
      'AED': 'د.إ',
      'SAR': 'ر.س',
      'TWD': 'NT$',
      'ARS': '$',
      'COP': '$',
      'VND': '₫',
      'EGP': 'E£',
      'NGN': '₦',
      'PKR': '₨',
      'BDT': '৳',
      'UAH': '₴',
      'RON': 'lei',
      'HUF': 'Ft',
      'BGN': 'лв',
      'HRK': 'kn',
      'ISK': 'kr',
      'QAR': 'ر.ق',
      'KWD': 'د.ك',
      'BHD': 'د.ب',
      'OMR': 'ر.ع',
      'JOD': 'د.ا',
      'LKR': 'Rs',
      'MMK': 'K',
      'KHR': '៛',
      'LAK': '₭',
      'NPR': 'Rs',
      'AFN': '؋',
      'IRR': '﷼',
      'IQD': 'ع.د',
      'SYP': '£',
      'LBP': 'ل.ل',
      'YER': '﷼',
      'LYD': 'ل.د',
      'TND': 'د.ت',
      'MAD': 'د.م.',
      'DZD': 'د.ج',
      'MUR': '₨',
      'GHS': '₵',
      'KES': 'KSh',
      'TZS': 'TSh',
      'UGX': 'USh',
      'ZMW': 'ZK',
      'BWP': 'P',
      'ETB': 'Br'
    };
  }

  /**
   * Set the initial currency on startup.
   * Uses the user-chosen localStorage value if present, otherwise defaults to USD.
   * globalCurrency from the API is applied separately via applyUserCurrency() after auth.
   */
  private setInitialCurrency(currencies: Currency[], _globalCurrency: string | null): void {
    const isUserChosen = localStorage.getItem(this.USER_CHOSEN_KEY) === 'true';
    const stored = isUserChosen ? this.getStoredCurrency() : null;

    if (stored && currencies.some(c => c.uuid === stored.uuid)) {
      this.currentCurrencySubject.next(stored);
      return;
    }

    const resolved = currencies.find(c => c.name === this.DEFAULT_CURRENCY_NAME) ?? currencies[0];
    if (resolved) {
      this.currentCurrencySubject.next(resolved as CurrencyPreference);
    }
  }

  private storeCurrency(currency: CurrencyPreference): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(currency));
    } catch (error) {
      console.warn('Failed to store currency preference:', error);
    }
  }

  private getStoredCurrency(): CurrencyPreference | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? (JSON.parse(stored) as CurrencyPreference) : null;
    } catch (error) {
      console.warn('Failed to retrieve stored currency:', error);
      return null;
    }
  }
}
